import React from 'react';
import { editor } from 'monaco-editor';
import { FrameworkClass } from '../../../framework/framework';
import { findFrameworkInitialization, getFrameworkDefaultValue, parseFrameworkValue } from '../../../framework/frameworkInitialization';
import { DeviceCardProps } from './DeviceCardProps';
import { Range } from 'monaco-editor';
import { getMainProgramEditorProxy } from '../utils/mainProgramEditor';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';

import HighTorqueMotor from './Motors/HighTorqueMotorCard';
import HighSpeedMotor from './Motors/HighSpeedMotorCard';
import BeamBreakSensor from './Sensors/BeamBreakSensorCard';
import DistanceSensor from './Sensors/DistanceSensorCard';
import MagneticProximitySwitchCard from './Sensors/MagneticProximitySwitchCard';
import BitAdapterCard from './Electronics/BitAdapterCard';
import DriveBase from './Mechanisms/DriveBaseCard';
import RotationalMechanism from './Mechanisms/RotationalMechanismCard';
import {
  findConstructorCallEndLine,
  findMatchingDelimiterIndex,
  getTextModelLines,
  resolveConstructorDeclarationLineIndex,
} from '../../../utils/pythonSourceScanUtils';

export type CardComponentList =
  | 'HighSpeedMotor'
  | 'HighTorqueMotor'
  | 'BeamBreakSensor'
  | 'DistanceSensor'
  | 'MagneticProximitySwitch'
  | 'BitAdapter'
  | 'DriveBase'
  | 'RotationalMechanism';

export interface ICardComponent {
  type: CardComponentList;
  props: DeviceCardProps;
}

export const CardComponents = {
  HighSpeedMotor,
  HighTorqueMotor,
  BeamBreakSensor,
  DistanceSensor,
  MagneticProximitySwitch: MagneticProximitySwitchCard,
  BitAdapter: BitAdapterCard,
  DriveBase,
  RotationalMechanism,
};

const DeviceCardBuilder = (block: ICardComponent) => {
  if (CardComponents[block.type]) {
    return React.createElement(CardComponents[block.type], block.props);
  }
  return null;
};

export default DeviceCardBuilder;

const getActiveEditor = (ed?: editor.IStandaloneCodeEditor): editor.IStandaloneCodeEditor | undefined => {
  return getMainProgramEditorProxy(ed ?? window.__bps_editor ?? undefined);
};

/** Monaco uses 1-based line numbers; line must be in [1, model.getLineCount()]. */
const isValidLine = (model: editor.ITextModel, lineOneBased: number): boolean =>
  Number.isFinite(lineOneBased) && lineOneBased >= 1 && lineOneBased <= model.getLineCount();

interface ConstructorArgMatch {
  argRange: Range;
  valueString: string;
}

const findConstructorArgValueEnd = (
  model: editor.ITextModel,
  startLineOneBased: number,
  valueStartCol0Based: number,
  endLineOneBased: number
): { line: number; col0: number } => {
  let depth = 0;
  let inString: '"' | "'" | null = null;
  let escaped = false;

  for (let lineNum = startLineOneBased; lineNum <= endLineOneBased; lineNum++) {
    const line = model.getLineContent(lineNum);
    const startCol = lineNum === startLineOneBased ? Math.max(valueStartCol0Based, 0) : 0;
    for (let i = startCol; i < line.length; i++) {
      const ch = line[i];
      if (inString) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (ch === '\\') {
          escaped = true;
          continue;
        }
        if (ch === inString) {
          inString = null;
        }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inString = ch;
        continue;
      }
      if (ch === '(' || ch === '[' || ch === '{') {
        depth++;
        continue;
      }
      if (ch === ')' || ch === ']' || ch === '}') {
        if (depth === 0) {
          return { line: lineNum, col0: i };
        }
        depth--;
        continue;
      }
      if (ch === ',' && depth === 0) {
        return { line: lineNum, col0: i };
      }
    }
  }

  const finalLine = Math.min(endLineOneBased, model.getLineCount());
  return { line: finalLine, col0: model.getLineContent(finalLine).length };
};

const findConstructorArgMatch = (
  model: editor.ITextModel,
  startLineOneBased: number,
  endLineOneBased: number,
  argName: string
): ConstructorArgMatch | null => {
  for (let lineNum = startLineOneBased; lineNum <= endLineOneBased; lineNum++) {
    const line = model.getLineContent(lineNum);
    let searchIndex = 0;
    while (searchIndex < line.length) {
      const parameterArg = line.indexOf(argName, searchIndex);
      if (parameterArg < 0) break;

      const before = parameterArg > 0 ? line[parameterArg - 1] : '';
      const after = parameterArg + argName.length < line.length ? line[parameterArg + argName.length] : '';
      const boundaryOk = !/[A-Za-z0-9_]/.test(before) && !/[A-Za-z0-9_]/.test(after);
      if (!boundaryOk) {
        searchIndex = parameterArg + argName.length;
        continue;
      }

      const equals = line.indexOf('=', parameterArg + argName.length);
      if (equals < 0) {
        searchIndex = parameterArg + argName.length;
        continue;
      }

      const endPos = findConstructorArgValueEnd(model, lineNum, equals + 1, endLineOneBased);
      const argRange = new Range(lineNum, parameterArg + 1, endPos.line, endPos.col0 + 1);
      const valueRange = new Range(lineNum, equals + 2, endPos.line, endPos.col0 + 1);
      const valueString = model.getValueInRange(valueRange).trim();
      return { argRange, valueString };
    }
  }

  return null;
};

export const initializeParameter = (props: DeviceCardProps): unknown => {
  const effectiveEditor = getActiveEditor(props.editor);
  const initParam = findFrameworkInitialization(props.parentClass, props.mechanismClass, props.parameterName);
  const resolveFrameworkDefaultDependency = (parameterName: string): unknown => {
    if (parameterName === props.parameterName) return null;
    return initializeParameter({ ...props, parameterName });
  };

  if (!initParam) {
    let value = initializeFromConstructorArgs(
      effectiveEditor,
      props.parentClass,
      props.mechanismClass,
      props.parameterName,
      props.lineNumber,
      props.parentName
    );
    if (value == null) {
      value = initializeFromPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, props.parentName);
    }
    if (value == null) {
      value = initializeFromFrameworkDefaults(props.parentClass, props.mechanismClass, props.parameterName, resolveFrameworkDefaultDependency);
    }
    return value;
  }

  const canConstructor = !!initParam.constructor_arg;
  const canProperty = !!(initParam.property || initParam.method);

  let value: unknown;

  // When both apply: use an existing property assignment if present; otherwise constructor kwargs.
  if (canProperty && canConstructor) {
    value = initializeFromPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, props.parentName);
    if (value == null) {
      value = initializeFromConstructorArgs(
        effectiveEditor,
        props.parentClass,
        props.mechanismClass,
        props.parameterName,
        props.lineNumber,
        props.parentName
      );
    }
  } else if (canConstructor) {
    value = initializeFromConstructorArgs(
      effectiveEditor,
      props.parentClass,
      props.mechanismClass,
      props.parameterName,
      props.lineNumber,
      props.parentName
    );
  } else if (canProperty) {
    value = initializeFromPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, props.parentName);
  }

  if (value == null) {
    value = initializeFromFrameworkDefaults(props.parentClass, props.mechanismClass, props.parameterName, resolveFrameworkDefaultDependency);
  }

  return value;
};

const initializeFromPropertyOrMethod = (
  editor: editor.IStandaloneCodeEditor | undefined,
  frameworkClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  statementPrefix: string
): unknown => {
  const initParam = findFrameworkInitialization(frameworkClass, childClass, parameter);
  if (initParam) {
    if (initParam.property || initParam.method) {
      const model = editor?.getModel();
      if (model) {
        const searchTag = statementPrefix.concat('.', initParam.property ?? initParam.method ?? '');
        const matches = model.findMatches(searchTag, false, false, true, null, false);
        if (matches.length > 0) {
          // Search for the value in the matches (use last one?)
          for (let i = matches.length - 1; i >= 0; i--) {
            const match = matches[i];
            if (!isValidLine(model, match.range.startLineNumber)) continue;
            const lineContent = model.getLineContent(match.range.startLineNumber);
            if (initParam.property) {
              const equals = lineContent.lastIndexOf('=');
              if (equals > 0) {
                const valueString = lineContent.substring(equals + 1);
                return parseFrameworkValue(valueString, String(initParam.type || ''));
              }
            } else if (initParam.method) {
              const start = lineContent.lastIndexOf('(');
              const end = lineContent.lastIndexOf(')');
              if (start > 0 && end > 0) {
                const valueString = lineContent.substring(start + 1, end);
                return parseFrameworkValue(valueString, String(initParam.type || ''));
              }
            }
          }
        }
      }
    } else if (initParam.class) {
      // Recursively search for the parameter in the child class
      const child = frameworkClass?.children?.find((item) => item.class === initParam.class);
      return initializeFromPropertyOrMethod(
        editor,
        initParam.class,
        undefined,
        parameter,
        child ? statementPrefix.concat('.', child.property) : statementPrefix
      );
    }
  } else if (childClass) {
    const child = frameworkClass?.children?.find((item) => item.class === childClass);
    return initializeFromPropertyOrMethod(
      editor,
      childClass,
      undefined,
      parameter,
      child ? statementPrefix.concat('.', child.property) : statementPrefix
    );
  }
  return null;
};

const initializeFromConstructorArgs = (
  editor: editor.IStandaloneCodeEditor | undefined,
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  lineNumber: number,
  parentName: string
): unknown => {
  const initParam = findFrameworkInitialization(parentClass, childClass, parameter);
  if (initParam && initParam.constructor_arg) {
    const model = editor?.getModel();
    if (model && model.getLineCount() < 1) return null;
    if (model && initParam.constructor_arg) {
      const constructorClassName = childClass?.name ?? parentClass?.name;
      const resolvedLineNumber =
        parentName.trim().length > 0
          ? resolveConstructorDeclarationLineIndex(getTextModelLines(model), parentName, {
              constructorClassName,
              fallbackLineIndex0Based: Number.isFinite(lineNumber) ? lineNumber : undefined,
            })
          : Number.isFinite(lineNumber)
          ? lineNumber
          : null;
      if (resolvedLineNumber === null) return null;

      const startLineOneBased = resolvedLineNumber + 1;
      if (!isValidLine(model, startLineOneBased)) return null;
      const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), model.getLineCount());
      const argName = initParam.constructor_arg;
      const match = findConstructorArgMatch(model, startLineOneBased, endLineOneBased, argName);
      if (match) {
        return parseFrameworkValue(match.valueString, String(initParam.type || ''));
      }
    }
  }
  return null;
};

const initializeFromFrameworkDefaults = (
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  resolveParameterValue?: (parameter: string) => unknown
): unknown => {
  return getFrameworkDefaultValue(parentClass, childClass, parameter, resolveParameterValue);
};

interface EditApplicationOptions {
  undoStop?: boolean;
  focusEditor?: boolean;
}

export const updateParameter = (
  props: DeviceCardProps,
  newValue: string,
  options: EditApplicationOptions = {}
): editor.IIdentifiedSingleEditOperation[] => {
  const effectiveEditor = getActiveEditor(props.editor);
  const model = effectiveEditor?.getModel();
  if (model && !isCurrentDeviceDeclarationLine(model, props.lineNumber, props.parentName)) return [];

  const initParam = findFrameworkInitialization(props.parentClass, props.mechanismClass, props.parameterName);
  const useConstructor = !!initParam?.constructor_arg;
  const useProperty = !!(initParam && (initParam.property || initParam.method));

  let edits: editor.IIdentifiedSingleEditOperation[] = [];

  const tryConstructorEdits = (): editor.IIdentifiedSingleEditOperation[] => {
    let e = searchForConstructorArgs(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.lineNumber);
    if (e.length === 0) {
      e = addConstructorArg(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.lineNumber);
    }
    return e;
  };

  const tryPropertyEdits = (): editor.IIdentifiedSingleEditOperation[] => {
    let e = searchForPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.parentName);
    if (e.length === 0) {
      e = addPropertyOrMethod(
        effectiveEditor,
        props.parentClass,
        props.mechanismClass,
        props.parameterName,
        newValue,
        props.parentName,
        props.lineNumber
      );
    }
    return e;
  };

  // When both apply: only change an existing property assignment if the user added one; otherwise use constructor (edit kwarg or insert). Do not insert a property line when the value lives only in the constructor.
  if (useConstructor && useProperty) {
    edits = searchForPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.parentName);
    if (edits.length === 0) {
      edits = tryConstructorEdits();
    }
  } else if (useConstructor) {
    edits = tryConstructorEdits();
  } else if (useProperty) {
    edits = tryPropertyEdits();
  }

  // Unknown parameter: preserve previous behavior (try constructor, then property).
  if (edits.length === 0 && !initParam) {
    edits = searchForConstructorArgs(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.lineNumber);
    if (edits.length === 0) {
      edits = addConstructorArg(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.lineNumber);
    }
    if (edits.length === 0) {
      edits = searchForPropertyOrMethod(effectiveEditor, props.parentClass, props.mechanismClass, props.parameterName, newValue, props.parentName);
      if (edits.length === 0) {
        edits = addPropertyOrMethod(
          effectiveEditor,
          props.parentClass,
          props.mechanismClass,
          props.parameterName,
          newValue,
          props.parentName,
          props.lineNumber
        );
      }
    }
  }

  const shouldPushUndoStop = options.undoStop !== false;
  if (shouldPushUndoStop) {
    effectiveEditor?.pushUndoStop();
  }
  effectiveEditor?.executeEdits('', edits);
  if (shouldPushUndoStop) {
    effectiveEditor?.pushUndoStop();
  }
  if (options.focusEditor !== false) {
    effectiveEditor?.focus();
  }

  return edits;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isPropertyAssignmentLine = (line: string, parentName: string, propertyName: string): boolean => {
  const escapedParent = escapeRegExp(parentName);
  const escapedProperty = escapeRegExp(propertyName);
  return new RegExp(`^\\s*${escapedParent}\\.${escapedProperty}\\s*=(?!=)`).test(line);
};

const isDirectDeviceAssignmentLine = (line: string, parentName: string): boolean => {
  const escapedParent = escapeRegExp(parentName);
  return new RegExp(`^${escapedParent}(?:\\.[A-Za-z_][\\w]*)+\\s*=(?!=)`).test(line);
};

const isDeviceDeclarationLine = (line: string, parentName: string): boolean => {
  const escapedParent = escapeRegExp(parentName);
  return new RegExp(`^${escapedParent}\\s*=`).test(line);
};

const isCurrentDeviceDeclarationLine = (model: editor.ITextModel, lineZeroBased: number, parentName: string): boolean => {
  const lineOneBased = lineZeroBased + 1;
  return (
    Boolean(parentName) &&
    Number.isFinite(lineZeroBased) &&
    isValidLine(model, lineOneBased) &&
    isDeviceDeclarationLine(model.getLineContent(lineOneBased), parentName)
  );
};

export const hasParameterAssignment = (props: DeviceCardProps, parameterName = props.parameterName): boolean => {
  const effectiveEditor = getActiveEditor(props.editor);
  const model = effectiveEditor?.getModel();
  if (!model) return false;

  const initParam = findFrameworkInitialization(props.parentClass, props.mechanismClass, parameterName);
  const propertyName = initParam?.property ?? initParam?.method ?? parameterName;
  const propertySearchTag = `${props.parentName}.${propertyName}`;
  const propertyMatches = model.findMatches(propertySearchTag, false, false, true, null, false);
  if (
    propertyMatches.some((match) => {
      const lineNum = match.range.startLineNumber;
      return isValidLine(model, lineNum) && isPropertyAssignmentLine(model.getLineContent(lineNum), props.parentName, propertyName);
    })
  ) {
    return true;
  }

  const constructorArg = initParam?.constructor_arg ?? parameterName;
  if (!Number.isFinite(props.lineNumber)) return false;

  const startLineOneBased = props.lineNumber + 1;
  if (!isValidLine(model, startLineOneBased)) return false;

  const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), model.getLineCount());
  return !!findConstructorArgMatch(model, startLineOneBased, endLineOneBased, constructorArg);
};

const DRIVE_BASE_SETTING_PARAMETERS = [
  'drive_type',
  'reference_frame',
  'max_translation_speed',
  'max_rotation_speed',
  'control_stick_drive_style',
  'wheel_diameter',
] as const;

interface ConstructorArgumentSpan {
  start: number;
  end: number;
  coreStart: number;
  coreEnd: number;
  name: string | null;
}

const findMatchingConstructorParens = (text: string): { open: number; close: number } | null => {
  const open = text.indexOf('(');
  if (open < 0) return null;
  const close = findMatchingDelimiterIndex(text, open, '(', ')');
  if (close < 0) return null;
  return { open, close };
};

const trimSpan = (text: string, start: number, end: number): { start: number; end: number } => {
  let coreStart = start;
  let coreEnd = end;
  while (coreStart < coreEnd && /\s/.test(text[coreStart])) coreStart++;
  while (coreEnd > coreStart && /\s/.test(text[coreEnd - 1])) coreEnd--;
  return { start: coreStart, end: coreEnd };
};

const findTopLevelEquals = (text: string, start: number, end: number): number => {
  let depth = 0;
  let inString: '"' | "'" | null = null;
  let escaped = false;

  for (let i = start; i < end; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === inString) {
        inString = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      inString = ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') {
      depth++;
      continue;
    }
    if (ch === ')' || ch === ']' || ch === '}') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (ch === '=' && depth === 0) {
      return i;
    }
  }

  return -1;
};

const parseTopLevelConstructorArguments = (text: string, open: number, close: number): ConstructorArgumentSpan[] => {
  const args: ConstructorArgumentSpan[] = [];
  let start = open + 1;
  let depth = 0;
  let inString: '"' | "'" | null = null;
  let escaped = false;

  const addArgument = (end: number) => {
    const { start: coreStart, end: coreEnd } = trimSpan(text, start, end);
    if (coreStart >= coreEnd) {
      start = end + 1;
      return;
    }
    const equals = findTopLevelEquals(text, coreStart, coreEnd);
    const name = equals >= 0 ? text.slice(coreStart, equals).trim() : null;
    args.push({ start, end, coreStart, coreEnd, name });
    start = end + 1;
  };

  for (let i = open + 1; i < close; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === inString) {
        inString = null;
      }
      continue;
    }

    if (ch === '"' || ch === "'") {
      inString = ch;
      continue;
    }
    if (ch === '(' || ch === '[' || ch === '{') {
      depth++;
      continue;
    }
    if (ch === ')' || ch === ']' || ch === '}') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (ch === ',' && depth === 0) {
      addArgument(i);
    }
  }
  addArgument(close);

  return args;
};

const removeConstructorArgs = (text: string, parameterNames: readonly string[]): string => {
  const parens = findMatchingConstructorParens(text);
  if (!parens) return text;

  const args = parseTopLevelConstructorArguments(text, parens.open, parens.close);
  const removeNames = new Set(parameterNames);
  const removeIndexes = args.map((arg, index) => (arg.name && removeNames.has(arg.name) ? index : -1)).filter((index) => index >= 0);
  if (removeIndexes.length === 0) return text;
  if (removeIndexes.length === args.length) {
    return text.slice(0, parens.open + 1) + text.slice(parens.close);
  }

  const ranges: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < removeIndexes.length; ) {
    const groupStart = removeIndexes[i];
    let groupEnd = groupStart;
    while (i + 1 < removeIndexes.length && removeIndexes[i + 1] === groupEnd + 1) {
      i++;
      groupEnd = removeIndexes[i];
    }

    if (groupEnd < args.length - 1) {
      ranges.push({ start: args[groupStart].start, end: args[groupEnd + 1].start });
    } else {
      ranges.push({ start: args[groupStart - 1].end, end: args[groupEnd].end });
    }
    i++;
  }

  let next = text;
  for (const range of ranges.sort((a, b) => b.start - a.start)) {
    next = next.slice(0, range.start) + next.slice(range.end);
  }

  return next.includes('\n') ? next : next.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
};

const getConstructorCloseIndent = (text: string, close: number): string => {
  const closeLineStart = text.lastIndexOf('\n', close) + 1;
  return text.slice(closeLineStart, close).match(/^\s*/)?.[0] ?? '';
};

const getConstructorArgIndent = (text: string, open: number, close: number, closeIndent: string): string => {
  const existingArgLine = text
    .slice(open + 1, close)
    .split('\n')
    .find((line) => line.trim().length > 0);
  return existingArgLine?.match(/^\s*/)?.[0] ?? `${closeIndent}    `;
};

const addConstructorArgToText = (text: string, argName: string, newValue: string): string => {
  const parens = findMatchingConstructorParens(text);
  if (!parens) return text;

  const statementFull = `${argName}=${newValue}`;
  const argsText = text.slice(parens.open + 1, parens.close);
  if (!argsText.includes('\n')) {
    const prefix = argsText.trim().length > 0 ? ', ' : '';
    return formatGeneratedPythonCall(text.slice(0, parens.close) + prefix + statementFull + text.slice(parens.close));
  }

  const closeIndent = getConstructorCloseIndent(text, parens.close);
  const argIndent = getConstructorArgIndent(text, parens.open, parens.close, closeIndent);
  const beforeClose = text.slice(0, parens.close).trimEnd();
  const needsComma = argsText.trim().length > 0 && !beforeClose.endsWith(',');
  return `${beforeClose}${needsComma ? ',' : ''}\n${argIndent}${statementFull},\n${closeIndent}${text.slice(parens.close)}`;
};

const replaceRangeInText = (text: string, range: Range, baseLineOneBased: number, replacement: string): string => {
  const lines = text.split('\n');
  const startLineIndex = range.startLineNumber - baseLineOneBased;
  const endLineIndex = range.endLineNumber - baseLineOneBased;
  if (startLineIndex < 0 || endLineIndex < startLineIndex || endLineIndex >= lines.length) return text;

  const before = lines[startLineIndex].slice(0, range.startColumn - 1);
  const after = lines[endLineIndex].slice(range.endColumn - 1);
  lines.splice(startLineIndex, endLineIndex - startLineIndex + 1, before + replacement + after);
  return lines.join('\n');
};

export const removeParameterAssignments = (
  props: DeviceCardProps,
  parameterNames: readonly string[],
  options: EditApplicationOptions = {}
): editor.IIdentifiedSingleEditOperation[] => {
  const effectiveEditor = getActiveEditor(props.editor);
  const model = effectiveEditor?.getModel();
  if (!model || model.getLineCount() < 1) return [];

  const edits: editor.IIdentifiedSingleEditOperation[] = [];
  const lineCount = model.getLineCount();

  const propertyAssignmentLines = new Set<number>();
  for (const parameterName of parameterNames) {
    const initParam = findFrameworkInitialization(props.parentClass, props.mechanismClass, parameterName);
    const propertyName = initParam?.property ?? parameterName;
    const searchTag = `${props.parentName}.${propertyName}`;
    const matches = model.findMatches(searchTag, false, false, true, null, false);
    for (const match of matches) {
      const lineNum = match.range.startLineNumber;
      if (isValidLine(model, lineNum) && isPropertyAssignmentLine(model.getLineContent(lineNum), props.parentName, propertyName)) {
        propertyAssignmentLines.add(lineNum);
      }
    }
  }

  // Remove property assignment lines (e.g. drive_base.reference_frame = ...)
  const sortedPropertyAssignmentLines = Array.from(propertyAssignmentLines).sort((a, b) => b - a);
  for (const lineNum of sortedPropertyAssignmentLines) {
    const range = lineNum < lineCount ? new Range(lineNum, 1, lineNum + 1, 1) : new Range(lineNum, 1, lineNum, model.getLineMaxColumn(lineNum));
    edits.push({ range, text: null, forceMoveMarkers: true });
  }

  // Remove stale constructor kwargs if present in existing user code
  if (Number.isFinite(props.lineNumber)) {
    const startLineOneBased = props.lineNumber + 1;
    if (isValidLine(model, startLineOneBased)) {
      const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), model.getLineCount());
      const constructorRange = new Range(startLineOneBased, 1, endLineOneBased, model.getLineMaxColumn(endLineOneBased));
      const constructorText = model.getValueInRange(constructorRange);
      const constructorArgNames = parameterNames.map((parameterName) => {
        const initParam = findFrameworkInitialization(props.parentClass, props.mechanismClass, parameterName);
        return initParam?.constructor_arg ?? parameterName;
      });
      const nextConstructorText = removeConstructorArgs(constructorText, constructorArgNames);
      if (nextConstructorText !== constructorText) {
        edits.push({
          range: constructorRange,
          text: nextConstructorText,
          forceMoveMarkers: true,
        });
      }
    }
  }

  if (edits.length === 0 || !effectiveEditor) return [];
  const shouldPushUndoStop = options.undoStop !== false;
  if (shouldPushUndoStop) {
    effectiveEditor.pushUndoStop();
  }
  effectiveEditor.executeEdits('', edits);
  if (shouldPushUndoStop) {
    effectiveEditor.pushUndoStop();
  }
  return edits;
};

export const removeReferenceFrameAssignments = (props: DeviceCardProps): editor.IIdentifiedSingleEditOperation[] =>
  removeParameterAssignments(props, ['reference_frame']);

export const removeDriveBaseSettingsAssignments = (props: DeviceCardProps): editor.IIdentifiedSingleEditOperation[] =>
  removeParameterAssignments(props, DRIVE_BASE_SETTING_PARAMETERS);

export const removeHardwareCardAssignments = (
  props: DeviceCardProps,
  options: EditApplicationOptions = {}
): editor.IIdentifiedSingleEditOperation[] => {
  const effectiveEditor = getActiveEditor(props.editor);
  const model = effectiveEditor?.getModel();
  if (!model || model.getLineCount() < 1 || !props.parentName) return [];

  const edits: editor.IIdentifiedSingleEditOperation[] = [];
  const lineCount = model.getLineCount();
  if (!Number.isFinite(props.lineNumber)) return [];

  if (!isCurrentDeviceDeclarationLine(model, props.lineNumber, props.parentName)) {
    return [];
  }

  const startLineOneBased = props.lineNumber + 1;
  const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), lineCount);
  const declarationRange =
    endLineOneBased < lineCount
      ? new Range(startLineOneBased, 1, endLineOneBased + 1, 1)
      : new Range(startLineOneBased, 1, endLineOneBased, model.getLineMaxColumn(endLineOneBased));

  const assignmentLines = new Set<number>();

  for (let lineNum = 1; lineNum <= lineCount; lineNum++) {
    if (isDirectDeviceAssignmentLine(model.getLineContent(lineNum), props.parentName)) {
      assignmentLines.add(lineNum);
    }
  }

  for (const lineNum of Array.from(assignmentLines).sort((a, b) => b - a)) {
    const range = lineNum < lineCount ? new Range(lineNum, 1, lineNum + 1, 1) : new Range(lineNum, 1, lineNum, model.getLineMaxColumn(lineNum));
    edits.push({ range, text: null, forceMoveMarkers: true });
  }

  edits.push({ range: declarationRange, text: null, forceMoveMarkers: true });

  if (edits.length === 0 || !effectiveEditor) return [];
  const shouldPushUndoStop = options.undoStop !== false;
  if (shouldPushUndoStop) {
    effectiveEditor.pushUndoStop();
  }
  effectiveEditor.executeEdits('', edits);
  if (shouldPushUndoStop) {
    effectiveEditor.pushUndoStop();
  }
  return edits;
};

const searchForPropertyOrMethod = (
  editor: editor.IStandaloneCodeEditor | undefined,
  frameworkClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  newValue: string,
  statementPrefix: string
): editor.IIdentifiedSingleEditOperation[] => {
  const initParam = findFrameworkInitialization(frameworkClass, childClass, parameter);
  if (initParam) {
    if (initParam.property || initParam.method) {
      const model = editor?.getModel();
      if (model) {
        let range = null;
        const searchTag = statementPrefix.concat('.', initParam.property ?? initParam.method ?? '');
        const matches = model.findMatches(searchTag, false, false, true, null, false);
        if (matches.length > 0) {
          const match = matches[0];
          range = match.range.setEndPosition(match.range.startLineNumber + 1, 0);

          // Update the statement
          const statementFull = initParam.property ? searchTag.concat(' = ', newValue) : searchTag.concat('(', newValue, ')');
          const edits: editor.IIdentifiedSingleEditOperation[] = [
            {
              range,
              text: statementFull + '\n',
              forceMoveMarkers: true,
            },
          ];

          // Remove duplicate statements
          for (let i = 1; i < matches.length; i++) {
            range = matches[i].range.setEndPosition(matches[i].range.startLineNumber + 1, 0);
            edits.push({
              range,
              text: null,
              forceMoveMarkers: true,
            });
          }

          return edits;
        }
      }
    } else if (initParam.class) {
      const child = frameworkClass?.children?.find((item) => item.class === initParam.class);
      return searchForPropertyOrMethod(
        editor,
        initParam.class,
        undefined,
        parameter,
        newValue,
        child ? statementPrefix.concat('.', child.property) : statementPrefix
      );
    }
  } else if (childClass) {
    const child = frameworkClass?.children?.find((item) => item.class === childClass);
    return searchForPropertyOrMethod(
      editor,
      childClass,
      undefined,
      parameter,
      newValue,
      child ? statementPrefix.concat('.', child.property) : statementPrefix
    );
  }
  return [];
};

const addPropertyOrMethod = (
  editor: editor.IStandaloneCodeEditor | undefined,
  frameworkClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  newValue: string,
  statementPrefix: string,
  lineNumber: number
): editor.IIdentifiedSingleEditOperation[] => {
  const initParam = findFrameworkInitialization(frameworkClass, childClass, parameter);
  if (initParam) {
    if (initParam.property || initParam.method) {
      const model = editor?.getModel();
      const constructorStartLine = lineNumber + 1;
      const rootStatementPrefix = statementPrefix.split('.')[0] ?? '';
      if (model && !isCurrentDeviceDeclarationLine(model, lineNumber, rootStatementPrefix)) {
        return [];
      }
      const constructorEndLine =
        model && isValidLine(model, constructorStartLine)
          ? Math.min(findConstructorCallEndLine(model, constructorStartLine), model.getLineCount())
          : constructorStartLine;
      const appendToEnd = model && isValidLine(model, constructorEndLine) && constructorEndLine >= model.getLineCount();
      const statementFull = initParam.property
        ? statementPrefix.concat('.', initParam.property, ' = ', newValue)
        : statementPrefix.concat('.', initParam.method ?? '', '(', newValue, ')');
      const range = appendToEnd
        ? new Range(constructorEndLine, model.getLineMaxColumn(constructorEndLine), constructorEndLine, model.getLineMaxColumn(constructorEndLine))
        : new Range(constructorEndLine + 1, 1, constructorEndLine + 1, 1);
      const text = appendToEnd ? `\n${statementFull}` : `${statementFull}\n`;
      const edits: editor.IIdentifiedSingleEditOperation[] = [
        {
          range,
          text,
          forceMoveMarkers: true,
        },
      ];

      return edits;
    } else if (initParam.class) {
      const child = frameworkClass?.children?.find((item) => item.class === initParam.class);
      return addPropertyOrMethod(
        editor,
        initParam.class,
        undefined,
        parameter,
        newValue,
        child ? statementPrefix.concat('.', child.property) : statementPrefix,
        lineNumber
      );
    }
  } else if (childClass) {
    const child = frameworkClass?.children?.find((item) => item.class === childClass);
    return addPropertyOrMethod(
      editor,
      childClass,
      undefined,
      parameter,
      newValue,
      child ? statementPrefix.concat('.', child.property) : statementPrefix,
      lineNumber
    );
  }
  return [];
};

const searchForConstructorArgs = (
  editor: editor.IStandaloneCodeEditor | undefined,
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass | undefined,
  parameter: string,
  newValue: string,
  lineNumber: number
): editor.IIdentifiedSingleEditOperation[] => {
  const initParam = findFrameworkInitialization(parentClass, childClass, parameter);

  if (initParam && initParam.constructor_arg) {
    const model = editor?.getModel();
    if (model && model.getLineCount() < 1) return [];
    if (model && initParam.constructor_arg) {
      const startLineOneBased = lineNumber + 1;
      if (!Number.isFinite(lineNumber) || !isValidLine(model, startLineOneBased)) return [];
      const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), model.getLineCount());
      const argName = initParam.constructor_arg;
      const match = findConstructorArgMatch(model, startLineOneBased, endLineOneBased, argName);
      if (match) {
        const statementFull = argName.concat('=', newValue);
        const constructorRange = new Range(startLineOneBased, 1, endLineOneBased, model.getLineMaxColumn(endLineOneBased));
        const constructorText = model.getValueInRange(constructorRange);
        const replacedConstructorText = replaceRangeInText(constructorText, match.argRange, startLineOneBased, statementFull);
        const nextConstructorText = formatGeneratedPythonCall(replacedConstructorText);
        return [
          {
            range: constructorRange,
            text: nextConstructorText,
            forceMoveMarkers: true,
          },
        ];
      }
    }
  }
  return [];
};
const addConstructorArg = (
  editor: editor.IStandaloneCodeEditor | undefined,
  parentClass: FrameworkClass | undefined,
  childClass: FrameworkClass,
  parameter: string,
  newValue: string,
  lineNumber: number
): editor.IIdentifiedSingleEditOperation[] => {
  const initParam = findFrameworkInitialization(parentClass, childClass, parameter);
  if (initParam && initParam.constructor_arg) {
    const model = editor?.getModel();
    if (model && initParam.constructor_arg) {
      const startLineOneBased = lineNumber + 1;
      if (!Number.isFinite(lineNumber) || !isValidLine(model, startLineOneBased)) return [];
      const endLineOneBased = Math.min(findConstructorCallEndLine(model, startLineOneBased), model.getLineCount());
      const constructorRange = new Range(startLineOneBased, 1, endLineOneBased, model.getLineMaxColumn(endLineOneBased));
      const constructorText = model.getValueInRange(constructorRange);
      const nextConstructorText = addConstructorArgToText(constructorText, initParam.constructor_arg, newValue);
      if (nextConstructorText === constructorText) return [];
      return [
        {
          range: constructorRange,
          text: nextConstructorText,
          forceMoveMarkers: true,
        },
      ];
    }
  }
  return [];
};
